import { useCallback, useEffect, useRef, useState } from 'react';

interface TooltipFlowState {
	showBatteryTooltip: boolean;
	showAnswerBlockTooltip: boolean;
	showLaunchConsoleTooltip: boolean;
}

interface UseTooltipFlowOptions {
	isFirstQuestion: boolean;
	hasGameStarted: boolean;
}

const LAUNCH_CONSOLE_TOOLTIP_DELAY = 3000; // 3 seconds

export const useTooltipFlow = ({ isFirstQuestion, hasGameStarted }: UseTooltipFlowOptions) => {
	const [tooltipState, setTooltipState] = useState<TooltipFlowState>({
		showBatteryTooltip: false,
		showAnswerBlockTooltip: false,
		showLaunchConsoleTooltip: false,
	});
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const clearTooltipTimeout = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	// Show battery tooltip when game starts on first question
	useEffect(() => {
		if (isFirstQuestion && hasGameStarted) {
			setTooltipState({
				showBatteryTooltip: true,
				showAnswerBlockTooltip: false,
				showLaunchConsoleTooltip: false,
			});
		}
	}, [isFirstQuestion, hasGameStarted]);

	// Called when answer block animation starts (before it appears)
	const onAnswerBlockAnimationStart = useCallback(() => {
		// For first question, show answer block tooltip
		if (isFirstQuestion) {
			setTooltipState({
				showBatteryTooltip: false,
				showAnswerBlockTooltip: true,
				showLaunchConsoleTooltip: false,
			});
		}

		// For all questions: start timer to show launch console tooltip after 3 seconds
		clearTooltipTimeout();
		timeoutRef.current = setTimeout(() => {
			setTooltipState({
				showBatteryTooltip: false,
				showAnswerBlockTooltip: false,
				showLaunchConsoleTooltip: true,
			});
		}, LAUNCH_CONSOLE_TOOLTIP_DELAY);
	}, [isFirstQuestion, clearTooltipTimeout]);

	const onAnswerBlockInteraction = useCallback(() => {
		if (!isFirstQuestion) return;

		// Clear the timeout since user interacted
		clearTooltipTimeout();

		setTooltipState({
			showBatteryTooltip: false,
			showAnswerBlockTooltip: false,
			showLaunchConsoleTooltip: true,
		});
	}, [isFirstQuestion, clearTooltipTimeout]);

	const hideAllTooltips = useCallback(() => {
		clearTooltipTimeout();
		setTooltipState({
			showBatteryTooltip: false,
			showAnswerBlockTooltip: false,
			showLaunchConsoleTooltip: false,
		});
	}, [clearTooltipTimeout]);

	useEffect(() => {
		return () => {
			clearTooltipTimeout();
		};
	}, [clearTooltipTimeout]);

	return {
		tooltipState,
		onAnswerBlockAnimationStart,
		onAnswerBlockInteraction,
		hideAllTooltips,
	};
};

export default useTooltipFlow;
